import { useRef, FC, useState, useEffect, useContext } from "react"
import Availability from "../scheduling/availability/Availability"
import Calendar from "../scheduling/calendar/Calendar"
import CalendarDetails from "../account-settings/calendars/CalendarDetails"
import CalendarLoadingSpinner from "../scheduling/calendar/CalendarLoadingSpinner"
import NewMeeting from "../scheduling/new-meeting/NewMeeting"
import useLoading from "../../hooks/useLoading"
import { Button } from "../ui/button"
import { CalendarRequests } from "../../api/app.service"
import { defaultAvailability } from "../../objects/availabilityObjects"
import { MeetingPrefillOptions, MeetingType } from "../../types/meetingTypes"
import { useLocation } from "react-router-dom"
import { SvgFilledInformationCircle } from "../icons"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  isTodayPage?: boolean
  isSchedulePage?: boolean
  pickerOpen?: any
  setPickerOpen?: any
}

const MemberScheduling: FC<Props> = ({
  isTodayPage = false,
  isSchedulePage = false,
  pickerOpen,
  setPickerOpen,
}) => {
  const { stopLoading } = useLoading()
  const { user } = useContext(UserCtx)
  const [isCalendarConnected, setIsCalendarConnected] = useState<boolean>(false)
  const [showCalendar, setShowCalendar] = useState<boolean>(true)
  const [showAvailability, setShowAvailability] = useState<boolean>(false)
  const [newMeetingType, setNewMeetingType] = useState<MeetingType>("custom")
  const [showNewMeeting, setShowNewMeeting] = useState<boolean>(false)
  const [showRescheduleMeeting, setShowRescheduleMeeting] =
    useState<boolean>(false)
  const [newMeetingPrefillOptions, setNewMeetingPrefillOptions] =
    useState<MeetingPrefillOptions | null>(null)
  const [hideSecondaryNav, setHideSecondaryNav] = useState<boolean>(false)
  const [userCalendars, setUserCalendars] = useState<any>({})
  const [userHasCalendar, setUserHasCalendar] = useState<boolean>(false)
  const [showConnectCalendar, setShowConnectCalendar] = useState<any>(false)
  const [userCalendarsLoading, setUserCalendarsLoading] =
    useState<boolean>(false)
  const [clickReschedule, setClickReschedule] = useState<boolean>(false)
  const [clickTodayReschedule, setClickTodayReschedule] =
    useState<boolean>(false)
  const [backToContacts, setBackToContacts] = useState<boolean>(false)
  const [keepBackToButton, setKeepBackToButton] = useState<boolean>(false)
  const [contact, setContact] = useState<any>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [showCalendarDetails, setShowCalendarDetails] = useState<boolean>(false)
  const { toast } = useToast()

  const showComponent = (callback: any) => {
    setShowCalendar(false)
    setShowAvailability(false)
    setShowNewMeeting(false)
    callback(true)
  }

  useEffect(() => {
    if (showNewMeeting) {
      showComponent(setShowNewMeeting)
      setHideSecondaryNav(true)
    } else {
      setHideSecondaryNav(false)
    }
  }, [showNewMeeting])

  const createDefaultAvailability = () => {
    if (user) {
      const userDefaultAvailability = {
        ...defaultAvailability,
        timezone: user.timezone,
      }
      CalendarRequests.setAvailabilityRules({
        user_id: parseInt(user.user_id),
        availability_rules: [userDefaultAvailability],
      })
        .then(() => {})
        .catch((ex) => {
          toast.error(ex.response.data.message)
          stopLoading()
        })
    }
  }

  const completeRegisterCalendar = (code: string, data: any) => {
    const redirectUrl = `${process.env.REACT_APP_BASE_URL}/account-settings/calendars`
    if (code && user) {
      CalendarRequests.registerCalendar({
        user_id: parseInt(user.user_id),
        profile_count: data.profile_count,
        calendar_connect_code: code,
        redirect_url: redirectUrl,
      })
        .then(() => {
          toast.success("Calendar connected!")
          createDefaultAvailability()
          fetchUserCalendars()
        })
        .catch((ex: any) => {
          toast.error(ex.response.data.message)
          stopLoading()
        })
      window.history.replaceState(null, "", window.location.pathname)
    }
  }

  const checkUrlParams = (data: any) => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")
    if (code && user) {
      completeRegisterCalendar(code, data)
    } else {
      window.history.replaceState(null, "", window.location.pathname)
      stopLoading()
    }
  }

  const fetchUserCalendars = () => {
    setUserCalendarsLoading(true)
    if (user) {
      CalendarRequests.getCalendarList({
        user_id: parseInt(user.user_id),
      })
        .then((data: any) => {
          setUserCalendarsLoading(false)
          setUserCalendars({
            profileCount: data.profile_count,
            calendars: data.calendars,
          })
          if (data.profile_count > 0) {
            setUserHasCalendar(true)
            setIsCalendarConnected(true)
          }
          checkUrlParams(data)
        })
        .catch((ex: any) => {
          setUserCalendarsLoading(false)
          toast.error(ex.response.data.message)
          setUserCalendarsLoading(false)
        })
    } else {
      stopLoading()
    }
  }

  // Check location state and populate new meeting if service and contact data was sent
  useEffect(() => {
    let state = location.state
    if (state) {
      if (state.service && state.contact) {
        setNewMeetingPrefillOptions({
          service: state.service,
          contact: state.contact,
        })
        setNewMeetingType("individual")
        setShowNewMeeting(true)
      }
      if (!state.service && state.contact) {
        setNewMeetingPrefillOptions({ contact: state.contact })
        setNewMeetingType("individual")
        setShowNewMeeting(true)
      }
    }
  }, [location])

  useEffect(() => {
    fetchUserCalendars()
  }, [user])

  const handleCalendarClick = () => {
    navigate("/account-settings/calendars")
  }

  return (
    <>
      {isTodayPage && (
        <div className="hidden xl:block relative  -mt-[68px] ml-[1073px]">
          <div style={{ height: "65px" }}></div>
        </div>
      )}
      {isTodayPage && (
        <>
          {userCalendarsLoading && <CalendarLoadingSpinner />}
          <div className={`mt-[12px] h-full flex w-full flex-col items-center`}>
            <>
              <div className="flex items-end w-full">
                {showCalendar && (
                  <Calendar
                    isTodayPage={isTodayPage}
                    pickerOpen={pickerOpen}
                    setPickerOpen={setPickerOpen}
                    userHasCalendar={userHasCalendar}
                    setHideSecondaryNav={setHideSecondaryNav}
                    setNewMeetingType={setNewMeetingType}
                    setShowNewMeeting={setShowNewMeeting}
                    showRescheduleMeeting={showRescheduleMeeting}
                    setShowRescheduleMeeting={setShowRescheduleMeeting}
                    clickReschedule={clickReschedule}
                    setClickReschedule={setClickReschedule}
                    clickTodayReschedule={clickTodayReschedule}
                    backToContacts={backToContacts}
                    contact={contact}
                    setKeepBackToButton={setKeepBackToButton}
                    keepBackToButton={keepBackToButton}
                    userCalendars={userCalendars}
                    isCalendarConnected={isCalendarConnected}
                  />
                )}
              </div>
            </>
          </div>
        </>
      )}

      {isSchedulePage && (
        <div className="container max-w-screen-lg">
          <div className="z-[200] mx-auto w-full flex flex-col bg-[#fcfcfc]">
            <div className="flex items-center ml-8 mb-2">
              <p className="font-bold text-[36px] text-left">My Calendar</p>
              <Button
                onClick={() => setShowCalendarDetails(true)}
                variant="link"
                size="icon"
                className="ml-2"
              >
                <SvgFilledInformationCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {userCalendarsLoading && <CalendarLoadingSpinner />}
          <div className={`mt-[12px] flex w-full flex-col items-center`}>
            <>
              <div className="w-full items-center justify-between px-[16px] md:flex md:max-w-[1112px]">
                {!hideSecondaryNav && !showRescheduleMeeting && (
                  <>
                    <div className="text-graySlate mb-[28px] mr-auto flex items-center justify-center gap-[12px] overflow-x-auto text-base font-bold md:mx-auto md:mb-0 md:ml-[20px] md:w-[1080px] md:justify-start">
                      <button
                        className={`${
                          showCalendar
                            ? "btn-primary btn-secondary bg-grayMist"
                            : "hover:bg-grayFlash rounded-[10px] duration-150"
                        } h-[40px] px-[16px] py-[8px] md:h-[48px] md:py-[12px]`}
                        onClick={handleCalendarClick}
                      >
                        Calendar
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div>
                {showCalendar && (
                  <Calendar
                    isSchedulePage={isSchedulePage}
                    userHasCalendar={userHasCalendar}
                    setHideSecondaryNav={setHideSecondaryNav}
                    setNewMeetingType={setNewMeetingType}
                    setShowNewMeeting={setShowNewMeeting}
                    showRescheduleMeeting={showRescheduleMeeting}
                    setShowRescheduleMeeting={setShowRescheduleMeeting}
                    clickReschedule={clickReschedule}
                    setClickReschedule={setClickReschedule}
                    clickTodayReschedule={clickTodayReschedule}
                    backToContacts={backToContacts}
                    contact={contact}
                    setKeepBackToButton={setKeepBackToButton}
                    keepBackToButton={keepBackToButton}
                    userCalendars={userCalendars}
                    isCalendarConnected={isCalendarConnected}
                  />
                )}
                {showAvailability && <Availability />}
                {showNewMeeting && (
                  <NewMeeting
                    setShowCalendar={setShowCalendar}
                    setShowNewMeeting={setShowNewMeeting}
                    newMeetingType={newMeetingType}
                    setNewMeetingType={setNewMeetingType}
                    prefillOptions={newMeetingPrefillOptions}
                    showRescheduleMeeting={showRescheduleMeeting}
                    setShowRescheduleMeeting={setShowRescheduleMeeting}
                    clickReschedule={clickReschedule}
                    setClickReschedule={setClickReschedule}
                    clickTodayReschedule={clickTodayReschedule}
                    backToContacts={backToContacts}
                    contact={contact}
                    keepBackToButton={keepBackToButton}
                  />
                )}
              </div>
            </>
          </div>
        </div>
      )}
      {showCalendarDetails && (
        <CalendarDetails setShowCalendarDetails={setShowCalendarDetails} />
      )}
    </>
  )
}

export default MemberScheduling
